import type { ApiClient } from "../../api";
import {
  deleteApiV1RolesByRoleAssignmentsByUser,
  deleteApiV1RolesByRole,
  deleteApiV1ScopesByScope,
  deleteApiV1ScopesByScopeMembershipsByMembership,
  deleteApiV1ScopesByScopeDepartmentsByDepartment,
  deleteApiV1UsersByUser,
  deleteApiV1UsersByUserAvatar,
  deleteApiV1UsersByUserSessionsBySession,
  deleteApiV1UsersByUserTwoFactorRequire,
  getApiV1AccessCheck,
  getApiV1AccessCriticalAdministrators,
  getApiV1Hierarchy,
  getApiV1HierarchyReach,
  getApiV1Permissions,
  getApiV1Roles,
  getApiV1RolesByRole,
  getApiV1OrganizationTree,
  getApiV1OrganizationUnitsByUnit,
  getApiV1Scopes,
  getApiV1ScopesByScopeDepartments,
  getApiV1ScopesByScopeMemberships,
  getApiV1Users,
  getApiV1UsersByUser,
  getApiV1UsersByUserEffectivePermissions,
  getApiV1UsersByUserHistory,
  getApiV1UsersByUserPermissionOverrides,
  getApiV1UsersByUserSessions,
  patchApiV1UsersByUser,
  patchApiV1RolesByRole,
  patchApiV1ScopesByScope,
  patchApiV1ScopesByScopeDepartmentsByDepartment,
  postApiV1Roles,
  postApiV1RolesByRoleAssignments,
  postApiV1AccessRightTransfers,
  postApiV1AccessRightTransfersPreview,
  postApiV1Scopes,
  postApiV1ScopesByScopeDepartments,
  postApiV1ScopesByScopeMemberships,
  postApiV1ScopesSelectAll,
  postApiV1ScopesByScopeSelect,
  postApiV1Users,
  postApiV1UsersByUserArchive,
  postApiV1UsersByUserBlock,
  postApiV1UsersByUserDisable,
  postApiV1UsersByUserRestore,
  postApiV1UsersByUserTwoFactorRequire,
  patchApiV1UsersByUserStatus,
  postApiV1UsersByUserUnblock,
  postApiV1UsersInvitations,
  putApiV1RolesByRolePermissions,
  putApiV1UsersByUserPermissionOverrides,
  putApiV1UsersByUserAvatar,
  putApiV1UsersByUserManager,
  type DeleteApiV1RolesByRoleAssignmentsByUserBody,
  type GetApiV1UsersQuery,
  type PatchApiV1RolesByRoleBody,
  type PatchApiV1ScopesByScopeBody,
  type PatchApiV1ScopesByScopeDepartmentsByDepartmentBody,
  type PostApiV1RolesBody,
  type PostApiV1RolesByRoleAssignmentsBody,
  type PostApiV1ScopesBody,
  type PostApiV1ScopesByScopeDepartmentsBody,
  type PostApiV1ScopesByScopeMembershipsBody,
} from "../../api/generated";
import type {
  AccessScope,
  AdminUser,
  Department,
  HierarchyNode,
  HierarchyReach,
  Membership,
  OrganizationNode,
  Permission,
  Role,
  AccessRightsTransferPlan,
  AccessRightsTransferResult,
  CriticalAdministrator,
  UserInput,
} from "./types";

/**
 * Где действует выдача или снятие доступа (ES-99): названная организация или
 * «весь холдинг». Без цели сервер берёт организацию из шапки, а в режиме «Все
 * организации» отвечает 409 — поэтому экраны передают цель всегда.
 */
export type GrantTarget = { scopeId: string } | { holdingWide: true };

function grantTargetBody(
  target: GrantTarget | undefined,
): { scope_id: string } | { holding_wide: true } | Record<string, never> {
  if (target === undefined) return {};
  return "holdingWide" in target
    ? { holding_wide: true }
    : { scope_id: target.scopeId };
}

export class AdministrationApi {
  constructor(private readonly client: ApiClient) {}

  listUsers(query: GetApiV1UsersQuery = {}) {
    return getApiV1Users(this.client, { query });
  }

  async user(id: string): Promise<AdminUser> {
    return (await getApiV1UsersByUser(this.client, { path: { user: id } }))
      .data;
  }

  history(id: string) {
    return getApiV1UsersByUserHistory(this.client, { path: { user: id } });
  }

  sessions(id: string) {
    return getApiV1UsersByUserSessions(this.client, { path: { user: id } });
  }

  endSession(userId: string, sessionId: string): Promise<void> {
    return deleteApiV1UsersByUserSessionsBySession(this.client, {
      path: { user: userId, session: sessionId },
    });
  }

  async overrides(id: string): Promise<Record<string, "allow" | "deny">> {
    return (
      await getApiV1UsersByUserPermissionOverrides(this.client, {
        path: { user: id },
      })
    ).data.overrides;
  }

  async assignAvatar(id: string, fileId: string): Promise<AdminUser> {
    return (
      await putApiV1UsersByUserAvatar(this.client, {
        path: { user: id },
        body: { file_id: fileId },
      })
    ).data;
  }

  async removeAvatar(id: string): Promise<AdminUser> {
    return (
      await deleteApiV1UsersByUserAvatar(this.client, {
        path: { user: id },
      })
    ).data;
  }

  async createUser(input: UserInput): Promise<AdminUser> {
    return (await postApiV1Users(this.client, { body: input })).data;
  }

  async inviteUser(
    input: Omit<UserInput, "password" | "password_confirmation">,
  ): Promise<AdminUser> {
    return (await postApiV1UsersInvitations(this.client, { body: input })).data;
  }

  async updateUser(
    id: string,
    input: Partial<
      Omit<UserInput, "email" | "password" | "password_confirmation">
    >,
  ): Promise<AdminUser> {
    return (
      await patchApiV1UsersByUser(this.client, {
        path: { user: id },
        body: input,
      })
    ).data;
  }

  deleteUser(id: string): Promise<void> {
    return deleteApiV1UsersByUser(this.client, { path: { user: id } });
  }

  async transitionUser(
    id: string,
    action: "block" | "unblock" | "disable" | "archive" | "restore",
  ): Promise<AdminUser> {
    const request = { path: { user: id } };
    switch (action) {
      case "block":
        return (await postApiV1UsersByUserBlock(this.client, request)).data;
      case "unblock":
        return (await postApiV1UsersByUserUnblock(this.client, request)).data;
      case "disable":
        return (await postApiV1UsersByUserDisable(this.client, request)).data;
      case "archive":
        return (await postApiV1UsersByUserArchive(this.client, request)).data;
      case "restore":
        return (await postApiV1UsersByUserRestore(this.client, request)).data;
    }
  }

  /**
   * Перевести сотрудника в другое рабочее состояние.
   *
   * Отпуск, командировка и больничный — не блокировка: человек остаётся в
   * системе, меняется только то, как его видно коллегам.
   */
  async setUserStatus(
    id: string,
    status: AdminUser["status"],
  ): Promise<AdminUser> {
    return (
      await patchApiV1UsersByUserStatus(this.client, {
        path: { user: id },
        body: { status },
      })
    ).data;
  }

  requireTwoFactor(id: string, required: boolean): Promise<unknown> {
    const request = { path: { user: id } };
    return required
      ? postApiV1UsersByUserTwoFactorRequire(this.client, request)
      : deleteApiV1UsersByUserTwoFactorRequire(this.client, request);
  }

  async listScopes(): Promise<AccessScope[]> {
    return (await getApiV1Scopes(this.client)).data;
  }

  /** Линейная иерархия сотрудников плоским списком: дерево собирает интерфейс. */
  async hierarchy(): Promise<HierarchyNode[]> {
    return (await getApiV1Hierarchy(this.client)).data;
  }

  /** Кому текущий человек вправе выдавать доступ и передавать исполнение. */
  async hierarchyReach(): Promise<HierarchyReach> {
    return (await getApiV1HierarchyReach(this.client)).data;
  }

  /** Ставит сотрудника под руководителя; null делает его корнем дерева. */
  async setManager(userId: string, managerId: string | null): Promise<void> {
    await putApiV1UsersByUserManager(this.client, {
      path: { user: userId },
      body: { manager_id: managerId },
    });
  }

  /** Оргструктура одним деревом: головная организация, КПО и их подразделения. */
  async organizationTree(): Promise<OrganizationNode[]> {
    return (await getApiV1OrganizationTree(this.client)).data;
  }

  /** Узел оргструктуры по идентификатору — организация или подразделение. */
  async organizationUnit(id: string): Promise<OrganizationNode> {
    return (
      await getApiV1OrganizationUnitsByUnit(this.client, { path: { unit: id } })
    ).data;
  }

  async createScope(input: PostApiV1ScopesBody): Promise<AccessScope> {
    return (await postApiV1Scopes(this.client, { body: input })).data;
  }

  async updateScope(
    id: string,
    input: PatchApiV1ScopesByScopeBody,
  ): Promise<AccessScope> {
    return (
      await patchApiV1ScopesByScope(this.client, {
        path: { scope: id },
        body: input,
      })
    ).data;
  }

  deleteScope(id: string): Promise<void> {
    return deleteApiV1ScopesByScope(this.client, { path: { scope: id } });
  }

  /** Смотреть все доступные организации сразу. */
  async selectAllScopes(): Promise<void> {
    await postApiV1ScopesSelectAll(this.client);
  }

  async selectScope(id: string): Promise<AccessScope> {
    return (
      await postApiV1ScopesByScopeSelect(this.client, {
        path: { scope: id },
      })
    ).data;
  }

  async listDepartments(scopeId: string): Promise<Department[]> {
    return (
      await getApiV1ScopesByScopeDepartments(this.client, {
        path: { scope: scopeId },
      })
    ).data;
  }

  async createDepartment(
    scopeId: string,
    input: PostApiV1ScopesByScopeDepartmentsBody,
  ): Promise<Department> {
    return (
      await postApiV1ScopesByScopeDepartments(this.client, {
        path: { scope: scopeId },
        body: input,
      })
    ).data;
  }

  async updateDepartment(
    scopeId: string,
    departmentId: string,
    input: PatchApiV1ScopesByScopeDepartmentsByDepartmentBody,
  ): Promise<Department> {
    return (
      await patchApiV1ScopesByScopeDepartmentsByDepartment(this.client, {
        path: { scope: scopeId, department: departmentId },
        body: input,
      })
    ).data;
  }

  deleteDepartment(scopeId: string, departmentId: string): Promise<void> {
    return deleteApiV1ScopesByScopeDepartmentsByDepartment(this.client, {
      path: { scope: scopeId, department: departmentId },
    });
  }

  async listMemberships(scopeId: string): Promise<Membership[]> {
    return (
      await getApiV1ScopesByScopeMemberships(this.client, {
        path: { scope: scopeId },
      })
    ).data;
  }

  async saveMembership(
    scopeId: string,
    input: PostApiV1ScopesByScopeMembershipsBody,
  ): Promise<Membership> {
    return (
      await postApiV1ScopesByScopeMemberships(this.client, {
        path: { scope: scopeId },
        body: input,
      })
    ).data;
  }

  deleteMembership(scopeId: string, membershipId: string): Promise<void> {
    return deleteApiV1ScopesByScopeMembershipsByMembership(this.client, {
      path: { scope: scopeId, membership: membershipId },
    });
  }

  async listRoles(): Promise<Role[]> {
    return (await getApiV1Roles(this.client)).data;
  }

  async role(id: string): Promise<Role> {
    return (await getApiV1RolesByRole(this.client, { path: { role: id } }))
      .data;
  }

  async updateRole(
    id: string,
    input: PatchApiV1RolesByRoleBody,
  ): Promise<Role> {
    return (
      await patchApiV1RolesByRole(this.client, {
        path: { role: id },
        body: input,
      })
    ).data;
  }

  deleteRole(id: string): Promise<void> {
    return deleteApiV1RolesByRole(this.client, { path: { role: id } });
  }

  async createRole(input: PostApiV1RolesBody): Promise<Role> {
    return (await postApiV1Roles(this.client, { body: input })).data;
  }

  async replaceRolePermissions(
    roleId: string,
    permissions: string[],
  ): Promise<Role> {
    return (
      await putApiV1RolesByRolePermissions(this.client, {
        path: { role: roleId },
        body: { permissions },
      })
    ).data;
  }

  assignRole(
    roleId: string,
    userId: string,
    departmentId?: PostApiV1RolesByRoleAssignmentsBody["department_id"],
    target?: GrantTarget,
  ): Promise<void> {
    return postApiV1RolesByRoleAssignments(this.client, {
      path: { role: roleId },
      body: {
        user_id: userId,
        ...(departmentId === undefined ? {} : { department_id: departmentId }),
        ...grantTargetBody(target),
      },
    });
  }

  revokeRole(
    roleId: string,
    userId: string,
    departmentId?: DeleteApiV1RolesByRoleAssignmentsByUserBody["department_id"],
    target?: GrantTarget,
  ): Promise<void> {
    const body = {
      ...(departmentId === undefined ? {} : { department_id: departmentId }),
      ...grantTargetBody(target),
    };
    return deleteApiV1RolesByRoleAssignmentsByUser(this.client, {
      path: { role: roleId, user: userId },
      ...(Object.keys(body).length === 0 ? {} : { body }),
    });
  }

  async permissions(): Promise<Permission[]> {
    return (await getApiV1Permissions(this.client)).data;
  }

  replaceOverrides(
    userId: string,
    overrides: Record<string, "allow" | "deny">,
    target?: GrantTarget,
  ) {
    return putApiV1UsersByUserPermissionOverrides(this.client, {
      path: { user: userId },
      body: { overrides, ...grantTargetBody(target) },
    });
  }

  effectivePermissions(
    userId: string,
    departmentUuid: string | null = null,
  ): Promise<{
    data: { permissions: string[]; department_id: string | null };
  }> {
    return getApiV1UsersByUserEffectivePermissions(this.client, {
      path: { user: userId },
      query: departmentUuid ? { department: departmentUuid } : {},
    });
  }

  check(
    permission: string,
    departmentUuid: string | null = null,
  ): Promise<{
    data: {
      permission: string;
      allowed: boolean;
      scope_id: string | null;
      department_id: string | null;
    };
  }> {
    return getApiV1AccessCheck(this.client, {
      query: {
        permission,
        ...(departmentUuid ? { department: departmentUuid } : {}),
      },
    });
  }

  async criticalAdministrators(): Promise<CriticalAdministrator[]> {
    return (await getApiV1AccessCriticalAdministrators(this.client)).data;
  }

  async previewTransfer(
    input: Parameters<typeof postApiV1AccessRightTransfersPreview>[1]["body"],
  ): Promise<AccessRightsTransferPlan> {
    return (
      await postApiV1AccessRightTransfersPreview(this.client, { body: input })
    ).data;
  }

  async commitTransfer(
    input: Parameters<typeof postApiV1AccessRightTransfers>[1]["body"],
    idempotencyKey: string,
  ): Promise<AccessRightsTransferResult> {
    return (
      await postApiV1AccessRightTransfers(this.client, {
        body: input,
        headers: { "Idempotency-Key": idempotencyKey },
      })
    ).data;
  }
}
